import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  IconButton,
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Avatar
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import Person from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useSelector } from 'react-redux';
import ClickableChip from '../../../../components/common/ClickableChip';
import { getOnboardingDashboard, inviteToDashboard } from '../../../../api/employeeApi';
import { getEntities } from '../../../../api/entityApi';
import { getLocations } from '../../../../api/locationApi';
import CustomNoResultsOverlay from '../employee-directory/components/CustomNoResultsOverlay';
import AlertDialog from '../../../../components/common/AlertDialog';
import useAlertDialog from '../../../../hooks/useAlertDialog';

const statusOptions = [
  { value: 'SENT', label: 'Sent' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'NEGOTIATING', label: 'Negotiating' },
  { value: 'EXPIRED', label: 'Expired' },
];

const filterInputStyle = {
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      borderColor: 'rgba(117, 34, 209, 0.15)',
      transition: 'all 0.2s ease-in-out',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(117, 34, 209, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#7522D1',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#4F4937',
    '&.Mui-focused': {
      color: '#7522D1',
    },
  },
};

const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string?.length || 0; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
};

const OfferStatus = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [entityFilter, setEntityFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState("ALL");
  
  const [entities, setEntities] = useState([]);
  const [locations, setLocations] = useState([]);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({
    joiningDate: '',
    salary: '',
    monthlyGross: '',
    employeeId: '',
  });
  const { alertDialog, showAlert, closeAlert } = useAlertDialog();

  const openMenu = Boolean(anchorEl);

  // Fetch Entities (Backend filters by user scope)
  useEffect(() => {
    const fetchEntitiesList = async () => {
      const res = await getEntities();
      if (res.success) setEntities(res.data);
    };
    if (isAuthenticated) fetchEntitiesList();
  }, [user, isAuthenticated]);

  // Fetch Locations when entity is selected
  useEffect(() => {
    const fetchLocationsList = async () => {
      if (entityFilter !== 'ALL') {
        const res = await getLocations({ entityId: entityFilter });
        if (res.success) setLocations(res.data);
        setLocationFilter('ALL'); // Reset location when entity changes
      } else {
        setLocations([]);
        setLocationFilter('ALL');
      }
    };
    fetchLocationsList();
  }, [entityFilter]);

  const handleStatusChange = (employeeId, newStatus) => {
    setEmployees(prevEmployees =>
      prevEmployees.map(emp =>
        emp.id === employeeId
          ? { ...emp, offerStatus: newStatus }
          : emp
      )
    );

  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    // Add your view details logic here
    handleMenuClose();
  };

  const validateAndInvite = () => {
    if (!selectedRow) return;

    // Check if fields are missing in the selected row
    const needsInfo = !selectedRow.joiningDate || !selectedRow.salary || selectedRow.salary === '-' || !selectedRow.empNo || selectedRow.empNo === '-';

    if (needsInfo) {
      const salary = selectedRow.salary === '-' ? '' : selectedRow.salary.replace('₹', '').replace(/,/g, '');
      setInviteData({
        joiningDate: selectedRow.joiningDate || '',
        salary: salary,
        monthlyGross: salary ? Math.round(Number(salary) / 12) : '',
        employeeId: selectedRow.empNo === '-' ? '' : selectedRow.empNo,
      });
      setInviteDialogOpen(true);
      handleMenuClose();
    } else {
      handleInviteDashboard();
    }
  };

  const handleInviteDashboard = async (finalData = null) => {
    const row = selectedRow || null;
    if (!row) return;

    try {
      setLoading(true);
      const dataToPass = finalData || {};
      const response = await inviteToDashboard(row.id, dataToPass);
      if (response.success) {
        showAlert({
          title: 'Success',
          message: `Successfully invited ${row.name} to the dashboard!`,
          severity: 'success',
          onConfirm: () => window.location.reload()
        });
      } else {
        showAlert({
          title: 'Failed to Invite',
          message: `Failed to invite: ${response.message}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error inviting to dashboard:', error);
      showAlert({
        title: 'Error',
        message: 'An error occurred while inviting to the dashboard.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setInviteDialogOpen(false);
      handleMenuClose();
      setSelectedRow(null);
    }
  };

  const handleInviteSubmit = () => {
    if (!inviteData.joiningDate || !inviteData.salary || !inviteData.employeeId) {
      showAlert({
        title: 'Missing Required Fields',
        message: 'Please fill all required fields: Joining Date, Salary, and Employee ID.',
        severity: 'warning'
      });
      return;
    }
    handleInviteDashboard(inviteData);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, statusFilter, entityFilter, locationFilter, employeeTypeFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getOnboardingDashboard({ 
        showInOfferStatus: true,
        search: searchQuery,
        status: statusFilter,
        entityId: entityFilter,
        workLocation: locationFilter,
        employmentType: employeeTypeFilter
      });
      if (response.success) {
        const formatted = response.data.map(emp => {
          const offer = emp.onboardingSummary?.offer || {};
          return {
            id: emp.id,
            empNo: emp.employeeId,
            name: emp.name,
            email: emp.email,
            offerStatus: (offer.status || "SENT").toUpperCase(),
            offerDate: offer.offerDate || null,
            acceptanceDeadline: offer.deadline || null,
            position: emp.role || "-",
            department: emp.department || "-",
            salary: offer.salary ? `₹${offer.salary.toLocaleString()}` : "-",
            ctc: offer.salary ? `₹${Number(offer.salary).toLocaleString()}` : "-",
            monthlyGross: offer.monthlyGross ? `₹${Number(offer.monthlyGross).toLocaleString()}` : (offer.salary ? `₹${Math.round(Number(offer.salary) / 12).toLocaleString()}` : "-"),
            joiningDate: offer.joiningDate || null,
            responseDate: offer.responseDate || null,
          };
        });
        setEmployees(formatted);
      }
    } catch (error) {
      console.error("Error fetching offer status data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Transform data to include id field required by DataGrid
  const rows = employees;

  const columns = [
    {
      field: 'empNo',
      headerName: 'EMPLOYEE NO.',
      width: 150,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: "#7522D1",
            fontWeight: 600,
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => navigate(`/my-team/employee-directory/${params.row.id}`)}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'name',
      headerName: 'NAME',
      width: 220,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const name = params.value || "";
        const initials = name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .substring(0, 2)
          .toUpperCase() || "EE";
        const avatarColor = stringToColor(name);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: "0.85rem",
                fontWeight: 600,
                bgcolor: avatarColor,
                color: "#FFFFFF",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              {initials}
            </Avatar>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#030007" }}>
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'email',
      headerName: 'EMAIL',
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'position',
      headerName: 'POSITION',
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'department',
      headerName: 'DEPARTMENT',
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'offerStatus',
      headerName: 'OFFER STATUS',
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <ClickableChip
          status={params.value}
          statusOptions={statusOptions}
          allowUpdate={true}  // Enable updates for offer status
          onStatusChange={(newStatus) => handleStatusChange(params.row.id, newStatus)}
        />
      ),
    },

    {
      field: 'ctc',
      headerName: 'CTC (ANNUAL)',
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'monthlyGross',
      headerName: 'MONTHLY GROSS',
      width: 180,
      resizable: true,
      sortable: true,
      filterable: false,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      resizable: false,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderLeft: "6px solid #7522D1",
          padding: "20px 24px",
          marginBottom: 3,
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(117, 34, 209, 0.05)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          border: "1px solid rgba(117, 34, 209, 0.08)",
          borderLeftWidth: "6px",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#030007",
              marginBottom: 0.5,
              fontSize: "1.15rem",
              letterSpacing: "0.2px",
            }}
          >
            Offer Status Tracking
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6E6855",
              fontSize: "0.9rem",
              fontWeight: 400,
            }}
          >
            Manage candidate offer stages, review acceptance deadlines, and invite successful hires directly to Siera's employee dashboard.
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "16px",
          border: "1px solid rgba(117, 34, 209, 0.08)",
          backgroundColor: "#FFF",
          boxShadow: "0 4px 20px rgba(117, 34, 209, 0.02)",
          mb: 3,
        }}
      >
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              placeholder="Search by Employee ID, Name, Email..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={filterInputStyle}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "rgba(117, 34, 209, 0.4)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {(user?.role?.hierarchyLevel === 'Level 1' || user?.role?.code === 'SUPER_ADMIN') && (
            <>
              <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
                <FormControl size="small" fullWidth sx={filterInputStyle}>
                  <InputLabel>Entity</InputLabel>
                  <Select
                    value={entityFilter}
                    label="Entity"
                    onChange={(e) => setEntityFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Entities</MenuItem>
                    {entities.map(ent => (
                      <MenuItem key={ent.id || ent._id} value={ent.id || ent._id}>{ent.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
                <FormControl size="small" fullWidth disabled={entityFilter === 'ALL'} sx={filterInputStyle}>
                  <InputLabel>Work Location</InputLabel>
                  <Select
                    value={locationFilter}
                    label="Work Location"
                    onChange={(e) => setLocationFilter(e.target.value)}
                  >
                    <MenuItem value="ALL">All Locations</MenuItem>
                    {locations.map(loc => (
                      <MenuItem key={loc.id || loc._id} value={loc.id || loc._id}>{loc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
            <FormControl size="small" fullWidth sx={filterInputStyle}>
              <InputLabel>Employee Type</InputLabel>
              <Select
                value={employeeTypeFilter}
                label="Employee Type"
                onChange={(e) => setEmployeeTypeFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="FULL_TIME">Full Time</MenuItem>
                <MenuItem value="PART_TIME">Part Time</MenuItem>
                <MenuItem value="CONTRACT">Contract</MenuItem>
                <MenuItem value="INTERN">Intern</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item size={{ xs: 12, sm: 6, md: 2.25 }}>
            <FormControl size="small" fullWidth sx={filterInputStyle}>
              <InputLabel>Offer Status</InputLabel>
              <Select
                value={statusFilter}
                label="Offer Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                {statusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ height: 540, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          rowHeight={64}
          hideFooter={false}
          slots={{
            noRowsOverlay: CustomNoResultsOverlay,
          }}
          sx={{
            border: "none",
            borderRadius: "16px",
            boxShadow: "0 4px 20px rgba(117, 34, 209, 0.04)",
            backgroundColor: "white",
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#F8F5FC',
              borderBottom: '1px solid rgba(117, 34, 209, 0.08)',
              fontWeight: 600,
              fontSize: '0.85rem',
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                color: '#42374F',
              },
            },
            "& .MuiDataGrid-row": {
              alignItems: "center !important",
              transition: "all 0.2s ease",
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.9rem',
              borderBottom: '1px solid rgba(117, 34, 209, 0.04)',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(117, 34, 209, 0.02)',
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid rgba(117, 34, 209, 0.08)",
              backgroundColor: "#F8F5FC",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: {
            borderRadius: 2,
            minWidth: 200,
            mt: 1,
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewDetails}
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(117, 34, 209, 0.08)',
              color: '#7522D1',
              '& .MuiListItemIcon-root': {
                color: '#7522D1',
              },
            },
          }}
        >
          <ListItemIcon>
            <Person sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText primary="View Details" primaryTypographyProps={{ variant: 'body2' }} />
        </MenuItem>
        <MenuItem onClick={validateAndInvite}
          sx={{
            color: '#7522D1',
            '&:hover': {
              backgroundColor: 'rgba(117, 34, 209, 0.12)',
            },
          }}
        >
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Invite Dashboard" primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }} />
        </MenuItem>
      </Menu>

      {/* Invite Details Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3, px: 3 }}>
          <Typography variant="h6" fontWeight={700} color="#030007">
            Complete Employee Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide the following details before inviting <b>{selectedRow?.name}</b> to the dashboard.
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Employee ID"
                placeholder="e.g. EMP001"
                value={inviteData.employeeId}
                onChange={(e) => setInviteData({ ...inviteData, employeeId: e.target.value })}
                required
                sx={filterInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Joining Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={inviteData.joiningDate}
                onChange={(e) => setInviteData({ ...inviteData, joiningDate: e.target.value })}
                required
                sx={filterInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Salary (Annual CTC)"
                placeholder="e.g. 1500000"
                type="number"
                value={inviteData.salary}
                onChange={(e) => {
                  const val = e.target.value;
                  setInviteData({ 
                    ...inviteData, 
                    salary: val,
                    monthlyGross: val ? Math.round(Number(val) / 12) : ''
                  });
                }}
                required
                sx={filterInputStyle}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Monthly Gross"
                placeholder="e.g. 125000"
                type="number"
                value={inviteData.monthlyGross}
                onChange={(e) => setInviteData({ ...inviteData, monthlyGross: e.target.value })}
                required
                sx={filterInputStyle}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1.5 }}>
          <Button 
            onClick={() => setInviteDialogOpen(false)} 
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              color: "#4F4937",
              fontWeight: 600,
              paddingX: 2.5,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleInviteSubmit}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              backgroundColor: "#7522D1",
              color: "#FFFFFF",
              fontWeight: 600,
              paddingX: 3,
              boxShadow: "0 4px 12px rgba(117, 34, 209, 0.2)",
              "&:hover": {
                backgroundColor: "#5C17A8",
                boxShadow: "0 6px 16px rgba(117, 34, 209, 0.3)",
              }
            }}
          >
            Invite to Dashboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={closeAlert}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
        confirmText={alertDialog.confirmText}
        cancelText={alertDialog.cancelText}
        showCancel={alertDialog.showCancel}
        onConfirm={alertDialog.onConfirm}
      />
    </Box>
  );
};

export default OfferStatus;